// @ts-strict-ignore
import { Howl } from 'howler'
import { PlaylistItem, Collection, AudioSectionContainer, SCREEN_SECTION_TYPE_AUDIO } from 'mytours-read-common'
import { GetScreensShowResponseSectionItems } from 'mytours-read-api-typescript'
import { EventActionsEnum, EventParams, sendEvent } from '../logging/analytics'
import { calculatePercentage } from './calculatePercentage'

export { calculatePercentage }

// The page shell initialises this global (mytours-web's mobile_web layout, or
// index.html in standalone dev), but the shell lives in a different repo —
// self-initialise so audio survives a shell regression.
const audioFiles = (): any[] => ((window as any).stqryAudioFiles ||= [])

export const setCurrentSound = (
	audioId: number,
	container: AudioSectionContainer,
	fileId: number,
	sound: Howl,
): void => {
	const audioIndex = audioFiles().findIndex(
		item =>
			item?.audioId === audioId &&
			item?.container?.id === container.id &&
			item?.container?.type === container.type &&
			item?.fileId === fileId,
	)

	const intemObject = {
		fileId,
		audioId,
		container,
		sound,
	}

	if (audioIndex > -1) {
		// don't replace the current audio object, because it causes player issues
	} else {
		audioFiles().push(intemObject)
	}
}

export const getCurrentSound = (
	audioId: number,
	container: AudioSectionContainer,
	fileId: number,
): Howl | undefined => {
	// Callers routinely destructure `currentAudio || {}` (a control event can race
	// a track switch), so tolerate missing inputs here — every caller already
	// handles an undefined return.
	if (audioId == null || !container) {
		return undefined
	}

	return audioFiles().find(
		item =>
			item.audioId === audioId &&
			item?.container?.id === container.id &&
			item?.container?.type === container.type &&
			item?.fileId === fileId,
	)?.sound
}

export const getAllSounds = (): Howl[] => audioFiles().map(item => item.sound)

// Every registered Howl was created from one language's files, and
// setCurrentSound never replaces an existing entry — so when the app language
// changes the old-language sounds must be released here, or the first screen
// re-render after the switch keeps playing them (sc bug: English audio after
// switching to Chinese). Remounted audio sections recreate their sounds from
// the refetched data.
export const unloadAllSounds = (): void => {
	for (const item of audioFiles()) {
		item?.sound?.unload()
	}
	audioFiles().length = 0
}

// Default seek step (seconds) for the skip-back / skip-forward controls.
// The control icons (fak fa-skip-back-10 / fa-skip-forward-10) bake this number
// into their artwork, so keep them in sync if this changes.
export const DEFAULT_SKIP_SECONDS = 10

export const onBack = (
	audioId: number,
	container: AudioSectionContainer,
	fileId: number,
	skipSeconds: number,
	callback: (newTime: number) => void,
): void => {
	const currentSound = getCurrentSound(audioId, container, fileId)
	if (!currentSound) {
		return
	}

	const seconds = currentSound.seek()

	const newTime = seconds - skipSeconds < 0 ? 0 : seconds - skipSeconds

	currentSound.seek(newTime)

	callback(newTime)
}

export const onForward = (
	audioId: number,
	duration: number,
	container: AudioSectionContainer,
	fileId: number,
	skipSeconds: number,
	callback: (newTime: number) => void,
): void => {
	const currentSound = getCurrentSound(audioId, container, fileId)
	if (!currentSound) {
		return
	}

	const seconds = currentSound.seek()

	let newTime = seconds + skipSeconds > duration ? duration : seconds + skipSeconds

	if (newTime === duration) {
		newTime = 0
	}

	currentSound.seek(newTime)

	callback(newTime)
}

export const seekToPercentage = (
	audioId: number,
	percentage: number,
	duration: number,
	container: AudioSectionContainer,
	fileId: number,
): void => {
	// The negated form also rejects NaN, which would sail through
	// `percentage < 0 || percentage > 100` and end up in currentSound.seek(NaN).
	if (!(percentage >= 0 && percentage <= 100)) {
		return
	}

	const currentSound = getCurrentSound(audioId, container, fileId)

	// Streams keep duration === Infinity even once Playing (Howler reports it
	// verbatim), and Infinity is truthy — a plain `&& duration` would let
	// currentSound.seek(Infinity) through.
	if (currentSound && Number.isFinite(duration) && duration > 0) {
		const newTime = duration * (percentage / 100)

		currentSound.seek(newTime)
	}
}

export const getAdjacentAudioItem = (
	currentAudio: PlaylistItem,
	direction: -1 | 1,
): GetScreensShowResponseSectionItems | undefined => {
	const { container, audioTrack } = currentAudio || {}
	const sections = container?.screen?.sections
	if (!sections) return undefined
	const section = sections.find(s => s.id === container.id)
	if (!section?.section_items) return undefined
	const audioItems = section.section_items.filter(si => si.media_type === SCREEN_SECTION_TYPE_AUDIO)
	const currentIndex = audioItems.findIndex(si => si.id === audioTrack?.audio?.id)
	if (currentIndex < 0) return undefined
	return audioItems[currentIndex + direction]
}

export const switchTrack = (currentAudio: PlaylistItem, direction: -1 | 1): void => {
	const newItem = getAdjacentAudioItem(currentAudio, direction)
	if (!newItem || !newItem.file) return

	const { container, audioTrack } = currentAudio || {}
	const currentAudioId = audioTrack?.audio?.id
	const currentFileId = audioTrack?.audio?.file?.id

	const currentSound = currentAudioId && getCurrentSound(currentAudioId, container, currentFileId)
	if (currentSound) currentSound.stop()

	// the new Howl's `onplay` handler dispatches setPlayerStatus, which updates currentPlaylistId
	// so callers re-render with the new audio
	const newSound = getCurrentSound(newItem.id, container, newItem.file.id)
	if (newSound) newSound.play()
}

export const sendAnalyticsEvent = (
	currentAudio: PlaylistItem,
	collection: Collection,
	action: EventActionsEnum,
	options: EventParams = {},
): void => {
	const { audioTrack, container } = currentAudio || {}
	const { audio } = audioTrack || {}

	const sound = getCurrentSound(audio?.id, container, audio?.file?.id)
	const duration = sound?.duration()
	const currentTime = sound?.seek()
	const audioCurrentTime = options.audioCurrentTime && options.audioCurrentTime === -1 ? duration : currentTime

	// One-decimal truncation; clamps the NaN from a missing sound
	// (duration/currentTime undefined) to 0, matching calculatePercentage.
	const toTenthSecond = (seconds: number): number => {
		const truncated = Math.floor(seconds * 10) / 10
		return Number.isFinite(truncated) ? truncated : 0
	}

	sendEvent(action, {
		audioDuration: options.audioDuration || toTenthSecond(duration),
		audioTitle: audio?.name,
		audioCurrentTime: toTenthSecond(audioCurrentTime),
		audioPercent: options.audioPercent || calculatePercentage(currentTime, duration),
		collection: collection?.name,
		collectionId: collection?.id,
		itemName: container?.screen?.name,
		screenSection: `${container?.id}-${container?.title}`,
	})
}
